/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useContext, useEffect, useState } from 'react'
import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import TableHeadRow from '../../../../../components/table/TableHeadRow'
import { TableRow, TableHeadCell } from '../../../../../components/table/TableComponents'
import { useRcmTableData } from '../../../../../hooks/useRcmTableData'
import { RcmTable } from '../../../../../components/rcm-table/RcmTable'
import {
  TableMultiSelectionWrapper,
  SelectAllRows,
  SelectRow,
  TableMultiSelectionConsumer
} from '../../../../../components/rcm-table/TableMultiSelection'
import { PublishContext } from '../publish-context/PublishContext'
import { eosApiService } from '../../../../../services/api/api.eos'
import type { PdrFilter, DocumentIdEntry, PdrDocumentEntry } from '../types'

const PatientsTable: React.FC = () => {
  const publishContext = useContext(PublishContext)
  const { t: translate } = useTranslation()

  const { filter, searchString, siteFolderIds, documentIds, allPatientsSelected, allSitesData } =
    publishContext?.selectionsData ?? {
      filter: { pageStart: 1, pageSize: 10 } as PdrFilter,
      searchString: null,
      siteFolderIds: [] as number[],
      documentIds: [] as DocumentIdEntry[],
      allPatientsSelected: false,
      allSitesData: []
    }

  const tableData = useRcmTableData()
  const [filterData, setFilterData] = useState<PdrFilter>({ ...filter })
  const [isEntirePageSelected, setIsEntirePageSelected] = useState<boolean>(false)
  const [resetSelections, setResetSelections] = useState<boolean>(false)

  useEffect(() => {
    if (filter) {
      tableData?.applyFilter({ ...filterData })
    }
  }, [filterData])

  useEffect(() => {
    tableData?.applyFilter({ ...filterData })
  }, [siteFolderIds, searchString])

  useEffect(() => {
    if (allPatientsSelected || isEntirePageSelected) {
      setResetSelections(true)
      publishContext?.setSelectionsData((data) => ({ ...data, documentIds: [], allPatientsSelected: false }))
      setIsEntirePageSelected(false)
    }
  }, [tableData?.entries])

  const getResolvedSiteIds = (): number[] => {
    let sitesData = siteFolderIds
    if (siteFolderIds?.length === 1 && siteFolderIds[0] === -1) {
      sitesData = _.map(allSitesData, 'siteFolderId')
    }
    return sitesData
  }

  return (
    <div>
      <TableMultiSelectionWrapper
        isSelectTotalEnabled={true}
        preselectedIds={_.map(documentIds, 'document') as unknown as string[]}
        texts={{
          selectAllItemsText: 'PDR_MULTISELECT_SELECT_ALL_IN_SECTION',
          allItemsSelectedText: 'PDR_MULTISELECT_ALL_N_ITEMS_SELECTED'
        }}
        getTotalNumberFunction={() => {
          const sitesData = getResolvedSiteIds()
          return eosApiService
            .getPdrDocuments(searchString ?? '', filter?.pageStart, filter?.pageSize, sitesData)
            .then((response) => {
              const data = response as { totalElements: number }
              return data?.totalElements
            })
        }}
        isPdrFlow={true}
        onSelectAll={() => {
          publishContext?.setSelectionsData((data) => ({ ...data, allPatientsSelected: true }))
        }}
        onClearAll={() => {
          publishContext?.setSelectionsData((data) => ({ ...data, documentIds: [], allPatientsSelected: false }))
        }}
      >
        <RcmTable
          className="workflow-list-table chita-table patients-selection-table"
          HeadRow={
            <TableHeadRow>
              <th className="select-box-field" style={{ width: '5%' }}>
                <TableMultiSelectionConsumer>
                  {(tableMultiSelectionContext) => {
                    return (
                      <SelectAllRows
                        onChange={(e) => {
                          const { checked } = e.target
                          publishContext?.setSelectionsData((data) => ({
                            ...data,
                            documentIds: checked
                              ? (tableData?.entries as PdrDocumentEntry[])?.map((item) => ({
                                  document: item?.documentFsiId,
                                  site: item?.siteFolderId
                                }))
                              : []
                          }))
                          if (publishContext?.selectionsData?.allPatientsSelected && !checked) {
                            publishContext.setSelectionsData((data) => ({
                              ...data,
                              allPatientsSelected: false
                            }))
                          }
                          setIsEntirePageSelected(checked)
                          setResetSelections(false)
                          tableMultiSelectionContext?.setMultiSelectState((prevState) => {
                            return {
                              ...prevState,
                              loading: checked,
                              selectAllOnThisPage: checked,
                              selectedWholeResult: false,
                              selectedIds: checked ? prevState.currentlyDisplayedCheckboxes : []
                            }
                          })
                          if (checked) {
                            tableMultiSelectionContext?.showSelectAllContent()
                          }
                        }}
                        isDisabled={!!searchString?.length}
                        reset={resetSelections}
                      />
                    )
                  }}
                </TableMultiSelectionConsumer>
              </th>
              <TableHeadCell className="patients-table-data-cell">{translate('PATIENT')}</TableHeadCell>
              <TableHeadCell>{translate('EMTF_MD_SITE')}</TableHeadCell>
            </TableHeadRow>
          }
          Row={(props: { row: PdrDocumentEntry; index: number }) => {
            const row = props?.row
            return (
              <TableMultiSelectionConsumer>
                {(tableMultiSelectionContext) => {
                  return (
                    <TableRow {...props}>
                      <td className="clipped-cell with-checkbox select-patient" style={{ width: '5%' }}>
                        <SelectRow
                          row={{ id: String(row?.id) }}
                          disabled={false}
                          onChange={(e) => {
                            const { checked: isChecked } = e.target
                            let docIds: DocumentIdEntry[] = publishContext?.selectionsData?.documentIds ?? []
                            if (isChecked) {
                              docIds = [...docIds, { document: row.documentFsiId, site: row.siteFolderId }]
                              publishContext?.setSelectionsData((data) => ({
                                ...data,
                                documentIds: [...docIds]
                              }))
                            } else {
                              docIds = docIds?.filter((item) => item?.document !== row.documentFsiId)
                              publishContext?.setSelectionsData((data) => ({
                                ...data,
                                documentIds: [...docIds]
                              }))
                            }
                            if (publishContext?.selectionsData?.allPatientsSelected && !isChecked) {
                              publishContext.setSelectionsData((data) => ({
                                ...data,
                                allPatientsSelected: false
                              }))
                            }
                            tableMultiSelectionContext?.setMultiSelectState((prevState) => {
                              const allSelected = _.map(docIds, 'document') as unknown as string[]
                              const newSelectedRowsState = isChecked
                                ? _.union(prevState.selectedIds, [String(row.id)])
                                : prevState.selectedIds.filter((val) => val !== String(row.id))
                              const checkedAllOnThisPage =
                                isChecked &&
                                prevState.currentlyDisplayedCheckboxes.every((v) => newSelectedRowsState.includes(v))
                              if (checkedAllOnThisPage && !searchString?.length) {
                                tableMultiSelectionContext.showSelectAllContent()
                              }
                              return {
                                ...prevState,
                                selectedWholeResult: false,
                                selectAllOnThisPage: !searchString?.length && checkedAllOnThisPage,
                                selectedIds: allSelected
                              }
                            })
                          }}
                          allSelected={allPatientsSelected}
                        />
                      </td>
                      <td className="clipped-cell with-checkbox patients-table-data-cell" style={{ width: '50%' }}>
                        {row?.documentName}
                      </td>
                      <td className="clipped-cell with-checkbox" style={{ width: '45%' }}>
                        {row?.siteFolderName}
                      </td>
                    </TableRow>
                  )
                }}
              </TableMultiSelectionConsumer>
            )
          }}
        />
      </TableMultiSelectionWrapper>
    </div>
  )
}

export default PatientsTable
